import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Card,
  CardContent,
  LinearProgress,
  Tooltip,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LocationOn as LocationIcon,
  BatteryFull as BatteryIcon,
  Speed as SpeedIcon,
  Build as MaintenanceIcon,
  AttachMoney as MoneyIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { api } from '../../utils/api';
import { geocodeAndUpdateForm } from '../../utils/geocoding';

const VehicleFleet = () => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [geocoding, setGeocoding] = useState(false);
  const [formData, setFormData] = useState({
    // Dati base
    name: '',
    type: '',
    model: '',
    year: '',
    status: 'available',
    location: '',
    latitude: '',
    longitude: '',
    serialNumber: '',
    color: '',
    purchaseDate: '',
    notes: '',
    // Dati manutenzione
    lastMaintenanceDate: '',
    nextMaintenanceDate: '',
    maintenanceInterval: 30,
    totalKm: 0,
    batteryLevel: 100,
    condition: 'excellent',
    // Dati tecnici
    batteryCapacity: '',
    maxSpeed: '',
    range: '',
    weight: '',
    // Dati economici
    purchasePrice: '',
    currentValue: '',
    depreciationRate: ''
  });

  useEffect(() => {
    fetchVehicles();
  }, []);

  const fetchVehicles = async () => {
    try {
      setLoading(true);
      const response = await api.get('/rental-vehicles');
      setVehicles(response.data);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      setError('Errore nel caricamento del parco mezzi');
    } finally {
      setLoading(false);
    }
  };

  const handleAddVehicle = () => {
    setEditingVehicle(null);
    setFormData({
      name: '',
      type: '',
      model: '',
      year: '',
      status: 'available',
      location: '',
      latitude: '',
      longitude: '',
      serialNumber: '',
      color: '',
      purchaseDate: '',
      notes: '',
      lastMaintenanceDate: '',
      nextMaintenanceDate: '',
      maintenanceInterval: 30,
      totalKm: 0,
      batteryLevel: 100,
      condition: 'excellent',
      batteryCapacity: '',
      maxSpeed: '',
      range: '',
      weight: '',
      purchasePrice: '',
      currentValue: '',
      depreciationRate: ''
    });
    setDialogOpen(true);
  };

  const handleEditVehicle = (vehicle) => {
    setEditingVehicle(vehicle);
    setFormData({
      name: vehicle.name,
      type: vehicle.type,
      model: vehicle.model,
      year: vehicle.year,
      status: vehicle.status,
      location: vehicle.location,
      latitude: vehicle.latitude,
      longitude: vehicle.longitude,
      serialNumber: vehicle.serialNumber,
      color: vehicle.color,
      purchaseDate: vehicle.purchaseDate,
      notes: vehicle.notes,
      lastMaintenanceDate: vehicle.lastMaintenanceDate,
      nextMaintenanceDate: vehicle.nextMaintenanceDate,
      maintenanceInterval: vehicle.maintenanceInterval,
      totalKm: vehicle.totalKm,
      batteryLevel: vehicle.batteryLevel,
      condition: vehicle.condition,
      batteryCapacity: vehicle.batteryCapacity,
      maxSpeed: vehicle.maxSpeed,
      range: vehicle.range,
      weight: vehicle.weight,
      purchasePrice: vehicle.purchasePrice,
      currentValue: vehicle.currentValue,
      depreciationRate: vehicle.depreciationRate
    });
    setDialogOpen(true);
  };


  const handleGeocodeAddress = async () => {
    if (!formData.location) return;
    
    setGeocoding(true);
    try {
      const result = await geocodeAndUpdateForm(formData.location, setFormData);
      if (result) {
        setError(null);
      } else {
        setError('Indirizzo non trovato. Verifica che sia corretto.');
      }
    } catch (error) {
      console.error('Errore nel geocoding:', error);
      setError('Errore nel geocoding dell\'indirizzo');
    } finally {
      setGeocoding(false);
    }
  };

  const handleSaveVehicle = async () => {
    try {
      if (editingVehicle) {
        await api.put(`/rental-vehicles/${editingVehicle.id}`, formData);
      } else {
        await api.post('/rental-vehicles', formData);
      }
      setDialogOpen(false);
      fetchVehicles();
    } catch (error) {
      console.error('Error saving vehicle:', error);
      setError('Errore nel salvataggio del mezzo');
    }
  };

  const handleDeleteVehicle = async (vehicleId) => {
    if (window.confirm('Sei sicuro di voler eliminare questo mezzo?')) {
      try {
        await api.delete(`/rental-vehicles/${vehicleId}`);
        fetchVehicles();
      } catch (error) {
        console.error('Error deleting vehicle:', error);
        setError('Errore nell\'eliminazione del mezzo');
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'available': return 'success';
      case 'rented': return 'warning';
      case 'maintenance': return 'error';
      case 'retired': return 'default';
      case 'offline': return 'secondary';
      default: return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'available': return 'Disponibile';
      case 'rented': return 'Noleggiato';
      case 'maintenance': return 'Manutenzione';
      case 'retired': return 'Ritirato';
      case 'offline': return 'Offline';
      default: return status;
    }
  };

  const getConditionColor = (condition) => {
    switch (condition) {
      case 'excellent': return 'success';
      case 'good': return 'info';
      case 'fair': return 'warning';
      case 'poor': return 'error';
      default: return 'default';
    }
  };

  const getConditionLabel = (condition) => {
    switch (condition) {
      case 'excellent': return 'Eccellente';
      case 'good': return 'Buono';
      case 'fair': return 'Discreto';
      case 'poor': return 'Scadente';
      default: return condition;
    }
  };

  const getBatteryColor = (level) => {
    if (level >= 80) return 'success';
    if (level >= 50) return 'warning';
    return 'error';
  };

  const isMaintenanceDue = (nextMaintenanceDate) => {
    if (!nextMaintenanceDate) return false;
    const nextDate = new Date(nextMaintenanceDate);
    const today = new Date();
    return nextDate <= today;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">
          Parco Mezzi
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddVehicle}
          sx={{
            borderRadius: 2,
            boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
            '&:hover': {
              boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)',
              transform: 'translateY(-2px)'
            },
            transition: 'all 0.3s ease'
          }}
        >
          Aggiungi Mezzo
        </Button>
      </Box>

      {/* Statistiche */}
      <Grid container spacing={1} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={4} md={2.4}>
          <Card sx={{ minHeight: '100px' }}>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Totale Mezzi
              </Typography>
              <Typography variant="h5">
                {vehicles.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <Card sx={{ minHeight: '100px' }}>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Disponibili
              </Typography>
              <Typography variant="h5" color="success.main">
                {vehicles.filter(v => v.status === 'available').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <Card sx={{ minHeight: '100px' }}>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Noleggiati
              </Typography>
              <Typography variant="h5" color="warning.main">
                {vehicles.filter(v => v.status === 'rented').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <Card sx={{ minHeight: '100px' }}>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography color="textSecondary" gutterBottom variant="body2">
                In Manutenzione
              </Typography>
              <Typography variant="h5" color="error.main">
                {vehicles.filter(v => v.status === 'maintenance').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <Card sx={{ minHeight: '100px' }}>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Offline
              </Typography>
              <Typography variant="h5" color="secondary.main">
                {vehicles.filter(v => v.status === 'offline').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Modello</TableCell>
              <TableCell>Anno</TableCell>
              <TableCell>Stato</TableCell>
              <TableCell>Condizione</TableCell>
              <TableCell>Batteria</TableCell>
              <TableCell>Km Totali</TableCell>
              <TableCell>Ultima Posizione</TableCell>
              <TableCell>Manutenzione</TableCell>
              <TableCell>Azioni</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {vehicles.map((vehicle) => (
              <TableRow key={vehicle.id}>
                <TableCell>
                  <Box>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {vehicle.name}
                    </Typography>
                    {vehicle.serialNumber && (
                      <Typography variant="caption" color="text.secondary">
                        SN: {vehicle.serialNumber}
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    label={vehicle.type}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>{vehicle.model}</TableCell>
                <TableCell>{vehicle.year}</TableCell>
                <TableCell>
                  <Chip
                    label={getStatusLabel(vehicle.status)}
                    color={getStatusColor(vehicle.status)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={getConditionLabel(vehicle.condition)}
                    color={getConditionColor(vehicle.condition)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BatteryIcon fontSize="small" />
                    <Box sx={{ width: 60 }}>
                      <LinearProgress
                        variant="determinate"
                        value={vehicle.batteryLevel}
                        color={getBatteryColor(vehicle.batteryLevel)}
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                    </Box>
                    <Typography variant="caption">
                      {vehicle.batteryLevel}%
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {vehicle.totalKm} km
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocationIcon fontSize="small" color="primary" />
                    <Typography variant="body2">
                      {vehicle.location || 'N/A'}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  {vehicle.nextMaintenanceDate && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Prossima: {new Date(vehicle.nextMaintenanceDate).toLocaleDateString('it-IT')}
                      </Typography>
                      {isMaintenanceDue(vehicle.nextMaintenanceDate) && (
                        <Chip
                          label="Scaduta"
                          color="error"
                          size="small"
                          sx={{ mt: 0.5 }}
                        />
                      )}
                    </Box>
                  )}
                </TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    onClick={() => handleEditVehicle(vehicle)}
                    color="primary"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteVehicle(vehicle.id)}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog per aggiungere/modificare mezzo */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          {editingVehicle ? 'Modifica Mezzo' : 'Nuovo Mezzo'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
              <Tab label="Dati Base" />
              <Tab label="Manutenzione" />
              <Tab label="Specifiche Tecniche" />
              <Tab label="Dati Economici" />
            </Tabs>
          </Box>

          {/* Tab Dati Base */}
          {activeTab === 0 && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Nome"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Tipo</InputLabel>
                  <Select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    label="Tipo"
                  >
                    <MenuItem value="bike">Bicicletta</MenuItem>
                    <MenuItem value="ebike">E-bike</MenuItem>
                    <MenuItem value="scooter">Scooter</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Modello"
                  value={formData.model}
                  onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Anno"
                  type="number"
                  value={formData.year}
                  onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Stato</InputLabel>
                  <Select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    label="Stato"
                  >
                    <MenuItem value="available">Disponibile</MenuItem>
                    <MenuItem value="rented">Noleggiato</MenuItem>
                    <MenuItem value="maintenance">Manutenzione</MenuItem>
                    <MenuItem value="retired">Ritirato</MenuItem>
                    <MenuItem value="offline">Offline</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Numero di Serie"
                  value={formData.serialNumber}
                  onChange={(e) => setFormData({ ...formData, serialNumber: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Colore"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Data Acquisto"
                  type="date"
                  value={formData.purchaseDate}
                  onChange={(e) => setFormData({ ...formData, purchaseDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      fullWidth
                      label="Ultima Posizione (Indirizzo)"
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      placeholder="Es: Via Roma 123, Milano"
                    />
                    <Button
                      variant="outlined"
                      onClick={handleGeocodeAddress}
                      disabled={geocoding || !formData.location}
                      sx={{ minWidth: 'auto', px: 2 }}
                    >
                      {geocoding ? <CircularProgress size={20} /> : '📍'}
                    </Button>
                  </Box>
                </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Latitudine"
                  type="number"
                  value={formData.latitude}
                  onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Longitudine"
                  type="number"
                  value={formData.longitude}
                  onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Note"
                  multiline
                  rows={3}
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                />
              </Grid>
            </Grid>
          )}

          {/* Tab Manutenzione */}
          {activeTab === 1 && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Ultima Manutenzione"
                  type="date"
                  value={formData.lastMaintenanceDate}
                  onChange={(e) => setFormData({ ...formData, lastMaintenanceDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Prossima Manutenzione"
                  type="date"
                  value={formData.nextMaintenanceDate}
                  onChange={(e) => setFormData({ ...formData, nextMaintenanceDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Intervallo Manutenzione (giorni)"
                  type="number"
                  value={formData.maintenanceInterval}
                  onChange={(e) => setFormData({ ...formData, maintenanceInterval: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Km Totali"
                  type="number"
                  value={formData.totalKm}
                  onChange={(e) => setFormData({ ...formData, totalKm: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Livello Batteria (%)"
                  type="number"
                  value={formData.batteryLevel}
                  onChange={(e) => setFormData({ ...formData, batteryLevel: e.target.value })}
                  inputProps={{ min: 0, max: 100 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Condizione</InputLabel>
                  <Select
                    value={formData.condition}
                    onChange={(e) => setFormData({ ...formData, condition: e.target.value })}
                    label="Condizione"
                  >
                    <MenuItem value="excellent">Eccellente</MenuItem>
                    <MenuItem value="good">Buono</MenuItem>
                    <MenuItem value="fair">Discreto</MenuItem>
                    <MenuItem value="poor">Scadente</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          )}

          {/* Tab Specifiche Tecniche */}
          {activeTab === 2 && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Capacità Batteria"
                  value={formData.batteryCapacity}
                  onChange={(e) => setFormData({ ...formData, batteryCapacity: e.target.value })}
                  placeholder="es. 500Wh, 750Wh"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Velocità Massima (km/h)"
                  type="number"
                  value={formData.maxSpeed}
                  onChange={(e) => setFormData({ ...formData, maxSpeed: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Autonomia (km)"
                  type="number"
                  value={formData.range}
                  onChange={(e) => setFormData({ ...formData, range: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Peso (kg)"
                  type="number"
                  step="0.1"
                  value={formData.weight}
                  onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                />
              </Grid>
            </Grid>
          )}

          {/* Tab Dati Economici */}
          {activeTab === 3 && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Prezzo di Acquisto (€)"
                  type="number"
                  step="0.01"
                  value={formData.purchasePrice}
                  onChange={(e) => setFormData({ ...formData, purchasePrice: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Valore Attuale (€)"
                  type="number"
                  step="0.01"
                  value={formData.currentValue}
                  onChange={(e) => setFormData({ ...formData, currentValue: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Tasso di Ammortamento (% annuo)"
                  type="number"
                  step="0.01"
                  value={formData.depreciationRate}
                  onChange={(e) => setFormData({ ...formData, depreciationRate: e.target.value })}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Annulla</Button>
          <Button onClick={handleSaveVehicle} variant="contained">
            {editingVehicle ? 'Aggiorna' : 'Crea'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VehicleFleet;